import { Download, Eye } from "lucide-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { GoalBadge } from "@/components/goal-badge";
import { ScoreCell } from "@/components/score-cell";
import { TrajectoryDialog } from "@/components/trajectory-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAsync } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import type { AdminChallenge, AdminSubmission } from "@/lib/api";
import { adminApi } from "@/lib/api";
import { formatError, formatSeconds, formatSteps, formatTimestamp, shortId } from "@/lib/format";
import { compareRuns } from "@/lib/scoring";
import { useChallenge } from "@/routes/admin/challenge/use-challenge";
import { cn } from "@/lib/utils";

interface Row extends AdminSubmission {
  email: string;
}

/**
 * Every upload to a client-simulated challenge, one row each, newest first. A
 * server-simulated challenge has the Attempts page instead, where the session is the row.
 *
 * A TA sees the miss's distance, which the student is not told, and any trajectory,
 * whatever the challenge's `show_trajectory` says.
 */
export function AdminChallengeSubmissionsRoute() {
  const { id = "" } = useParams();
  const adminKey = useAdminKey();
  const [query, setQuery] = useState("");
  const [viewing, setViewing] = useState<string | null>(null);

  const submissions = useAsync(
    adminKey ? () => adminApi.challengeSubmissions(id, adminKey) : null,
    [id, adminKey],
  );
  // The section's parent already loaded the challenge.
  const challenge: AdminChallenge | undefined = useChallenge().challenge;

  const rows = useMemo<Row[]>(() => {
    if (!submissions.data) return [];
    return [...submissions.data].sort((a, b) => b.submitted_at - a.submitted_at);
  }, [submissions.data]);

  const filtered = rows.filter((row) =>
    row.email.toLowerCase().includes(query.toLowerCase()),
  );

  // Best per student, as the leaderboard picks; only `done` rows are eligible.
  const bestJobIds = useMemo(() => {
    if (!challenge) return new Set<string>();
    const best = new Map<string, Row>();
    for (const row of rows) {
      if (row.status !== "done") continue;
      const current = best.get(row.email);
      if (!current || compareRuns(row, current) < 0)
        best.set(row.email, row);
    }
    return new Set([...best.values()].map((r) => r.job_id));
  }, [rows, challenge]);

  function exportCsv() {
    const csv = [
      "email,submitted_at,job_id,points,max_points,reached,trans_error,nav_steps,wall_s,counted_for_leaderboard",
      ...rows.map((r) =>
        [
          r.email,
          new Date(r.submitted_at).toISOString(),
          r.job_id,
          r.score?.points ?? "",
          r.score?.max_points ?? "",
          r.score ? (r.score.reached ? "yes" : "no") : "",
          r.trans_error ?? "",
          r.nav_steps ?? "",
          r.wall_s ?? "",
          bestJobIds.has(r.job_id) ? "yes" : "no",
        ].join(","),
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `submissions-${id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by email"
          className="sm:max-w-xs"
        />
        <Button variant="outline" onClick={exportCsv} disabled={!rows.length}>
          <Download className="size-4" aria-hidden />
          Export CSV
        </Button>
        <p className="text-muted-foreground ml-auto text-sm">
          {rows.length} submission{rows.length === 1 ? "" : "s"} from{" "}
          {new Set(rows.map((row) => row.email)).size} student(s)
        </p>
      </div>

      <AsyncBoundary
        state={submissions}
        empty={<EmptyState title="No scored submissions" />}
      >
        {() =>
          filtered.length === 0 ? (
            <EmptyState title="No matches" />
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Student</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Goal</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead className="text-right">Steps</TableHead>
                    <TableHead className="text-right">Distance</TableHead>
                    <TableHead className="hidden text-right lg:table-cell">
                      Time
                    </TableHead>
                    <TableHead className="text-right">Trajectory</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.job_id}>
                      <TableCell>
                        <span className="font-medium">{row.email}</span>
                        {bestJobIds.has(row.job_id) ? (
                          <span className="bg-primary/15 text-primary ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                            best
                          </span>
                        ) : null}
                        <p className="text-muted-foreground font-mono text-xs">
                          {shortId(row.job_id)}
                        </p>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatTimestamp(row.submitted_at)}
                      </TableCell>
                      <TableCell>
                        <GoalBadge tier={row.goal_tier} />
                      </TableCell>
                      <TableCell className="text-right">
                        <ScoreCell
                          points={row.score?.points ?? null}
                          max={row.score?.max_points ?? challenge?.rubric_spec.max_points ?? 0}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatSteps(row.nav_steps)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right tabular-nums",
                          row.score && !row.score.reached && "text-muted-foreground",
                        )}
                        title={row.score && !row.score.reached ? "A miss; the student is not shown this" : undefined}
                      >
                        {formatError(row.trans_error)}
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden text-right tabular-nums lg:table-cell">
                        {formatSeconds(row.wall_s)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="View trajectory"
                          onClick={() => setViewing(row.job_id)}
                        >
                          <Eye className="size-4" aria-hidden />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        }
      </AsyncBoundary>

      <TrajectoryDialog
        jobId={viewing}
        challengeId={id}
        simulator={challenge?.simulator}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}
